import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';
import { Ban, ExternalLink, Wrench, X } from 'lucide-react';
import {
  Alert, Button, ConfirmDialog, IconButton, SystemPage, useToast,
} from '@parsina/framework';

import { useTranslation } from '../../i18n';
import usePageTitle from '../../hooks/usePageTitle';
import { useShortcut } from '../../lib/shortcuts.jsx';
import { useClinicContext, refOf } from '../../context/ClinicContext.jsx';
import { refreshAccessToken, refreshUntilRole } from '../../lib/auth.js';
import { bffMutation, bffQuery } from '../../lib/bffClient.js';
import { countryForPhone } from '../../constants/countries';
import { openDoctorPanel, doctorHomeUrl } from '../../lib/doctorPanel.js';
import { linkFounderAsPractitioner } from '../../lib/practitionerLink.js';
import { clinicEntryPath } from '../../lib/purchaseIntent.js';
import AuthShell, { useAuthShellOffline } from '../../components/auth/AuthShell.jsx';
import StepIndicator from '../../components/auth/StepIndicator.jsx';
import AccessRefreshPanel from '../../components/auth/AccessRefreshPanel.jsx';
import OnboardingSuccess from '../../components/auth/OnboardingSuccess.jsx';
import { PrimaryAction } from '../../components/auth/OnboardingParts.jsx';
import { DEFAULT_CLINIC_TYPE } from '../Clinic/Clinic.shared';
import ClinicDetailsStep, { DEFAULT_COUNTRY, validateClinicDraft } from './ClinicDetailsStep.jsx';
import ClinicRolesStep from './ClinicRolesStep.jsx';
import DoctorTrack, { fetchOfferedDraw } from './DoctorTrack.jsx';
import QuickSetup from './QuickSetup.jsx';
import QuickClinicNameStep from './QuickClinicNameStep.jsx';
import { describeError, RECOVERY } from './errors.js';
import { DEFAULT_PRESENCE_DAYS, presenceDaysForCountry, visitDeclarationInput } from './parsinaDefaults.js';
import { currencyForCountry, useAccountRegion } from './accountRegion.js';

/**
 * «ثبت کلینیک جدید» — clinic registration, in two steps.
 *
 * This screen used to live inside `App.jsx` as a three-hundred-line component
 * next to the router. Moving it out is not tidying: while it was there it had
 * no test seam, it was invisible to the per-screen i18n checks, and every
 * primitive in it was hand-rolled because reaching for the kit from the router
 * file felt like the wrong shape. The screen is where the screen belongs.
 *
 * ── The two steps, and why the second cannot be first ───────────────────────
 *
 * A clinic declares WHICH JOBS EXIST in it at the moment it is created, instead
 * of discovering weeks later that there is no role to give the receptionist it
 * just hired. But both of the roles step's calls are clinic-scoped, so the
 * clinic — and the token that can speak for it — must exist first. That is the
 * whole reason the token refresh between them is a visible step rather than a
 * swallowed `try/catch`: it is load-bearing, and when it fails the roles step
 * is guaranteed to 403.
 *
 * ── `?quick=` — registration with nothing to fill in (Iranian accounts) ─────
 *
 * `?quick=run` is the run «شروع» hands over to once it knows who this is
 * (quickSetup.js): it builds the clinic, applies Parsina's defaults and, for a
 * doctor, joins them to it — and enters. `?quick=1` is «پزشک نیستم»: the
 * clinic's name, then the same run. Neither touches the wizard below, which is
 * still what the switcher's «افزودن کلینیک» and a number from abroad get.
 *
 * ── Surviving a reload (E8) ─────────────────────────────────────────────────
 *
 * Phase is component state, so a refresh on step two used to drop the person
 * back onto an empty registration form for a clinic that already existed —
 * where the only button submits, and submitting collides on the primary key.
 * A breadcrumb in `sessionStorage` puts them back on the roles step instead.
 * `sessionStorage` rather than `localStorage` deliberately: it is scoped to
 * this tab and dies with it, which is right for a crumb whose whole purpose is
 * to survive one accidental reload.
 */

const RESUME_KEY = 'onboarding.pendingRoles.v1';

/** Step 3's choices onto the crumb: from here on a remount finishes, never re-asks (PM-0195). */
function rememberSetup(setup) {
  try {
    const crumb = JSON.parse(sessionStorage.getItem(RESUME_KEY) || 'null');
    if (crumb?.clinicId) sessionStorage.setItem(RESUME_KEY, JSON.stringify({ ...crumb, setup }));
  } catch { /* the crumb is a convenience, never a requirement */ }
}

const PHASE = {
  /** «من پزشک هستم» — the نظام پزشکی lookup, before any clinic exists. */
  DOCTOR: 'doctor',
  /** The wheel a campaign owes this doctor, when it owes one. */
  PRIZE: 'prize',
  /** The practitioner record exists; adopting the `doctor` role it granted. */
  DOCTOR_ADOPTING: 'doctor-adopting',
  /** The form. */
  DETAILS: 'details',
  /** The clinic exists; adopting the grants that came with it. */
  ADOPTING: 'adopting',
  /** Choosing which roles the clinic has. */
  ROLES: 'roles',
  /** Roles done; adopting those grants before entering. */
  ENTERING: 'entering',
  /** Doctor track only — joining the founder to the clinic they just built. */
  LINKING: 'linking',
  /** Doctor track only — both halves are done and the way in is a button. */
  DONE: 'done',
};

const CREATE_CLINIC = `mutation CreateClinic($input: CreateClinicInput!) {
  createClinic(input: $input) { id name officePhone }
}`;

/** The founder's own sign-in number — what preselects the clinic's country. */
const MY_PHONE = `query MyPhone { myProfile { phone } }`;

/**
 * The founder-doctor's hours and visit length, written on the membership the
 * join just created or found. Same mutation as the doctor's row on «همکاران».
 */
const UPDATE_DOCTOR_LINK = `mutation UpdateDoctorLink($id: ID!, $input: UpdateDoctorLinkInput!) {
  updateDoctorLink(id: $id, input: $input) { id visitDurationMinutes }
}`;

/** «ویزیت», re-declared under the founder-doctor — which is what switches it on. */
const DECLARE_VISIT = `mutation SetClinicServiceDeclaration($input: ClinicServiceDeclarationInput!) {
  setClinicServiceDeclaration(input: $input) { id isActive }
}`;

const emptyDraft = (currency = 'IRR', country = DEFAULT_COUNTRY) => ({
  name: '',
  clinicType: DEFAULT_CLINIC_TYPE,
  // The clinic's books, IRR unless the founder picks otherwise (CONTRACTS §7) —
  // or, for a founder whose number is from abroad, their country's currency.
  currency,
  // The clinic's jurisdiction (PM-0082). IR until the founder's own number
  // suggests otherwise — see `suggestedCountry` below.
  country,
  officePhone: '',
  province: '',
  city: '',
  address: '',
  latitude: null,
  longitude: null,
});

export default function OnboardingScreen({ onSuccess }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const offline = useAuthShellOffline();
  const {
    memberships, enterable, capabilities, refreshGrants, repairSetup, defaultRef, reload,
  } = useClinicContext();

  // Creating a clinic is an ADDITION, never a replacement (design §2.7/§5.4.1).
  // Knowing whether this person already has one changes two things: the wizard
  // offers a way back instead of reading like being logged out of the clinic
  // they were just in, and the copy says «افزودن» rather than «ایجاد».
  const isAdditional = enterable.length > 0;
  const founderRow = memberships.find((m) => m.isFounder);
  const halfBuilt = memberships.find((m) => m.repairNeeded);

  // ── «من پزشک هستم» ───────────────────────────────────────────────────────
  //
  // The doctor track is a QUERY PARAMETER, not a guess. Nothing in a token
  // minted for a fresh phone-number account says «doctor» — that role is
  // granted BY the step below, so reading it from the token would mean the
  // track could only ever be offered to someone who no longer needs it. The
  // «شروع» screen asks, and the answer travels in the URL so a reload during
  // registration stays on the track the person chose.
  //
  // It is not authorization either: `completeOnboardingStep1` refuses a council
  // number that سازمان نظام پزشکی does not hold, whoever asks. The parameter
  // decides which screens are shown, never who may be a doctor.
  const askedAsDoctor = params.get('doctor') === '1';
  const quick = params.get('quick');
  const [doctorTrack, setDoctorTrack] = React.useState(askedAsDoctor);
  const [phase, setPhase] = React.useState(() => (askedAsDoctor ? PHASE.DOCTOR : PHASE.DETAILS));
  const [draw, setDraw] = React.useState(null);
  const [createdId, setCreatedId] = React.useState('');
  const [createdName, setCreatedName] = React.useState('');
  // Under a brand outside the Iranian market (`market: 'intl'`) nothing in the
  // wizard starts Iranian: the currency starts where a founder from an unknown
  // country starts (USD, never IRR) and the jurisdiction starts EMPTY, to be
  // filled from the founder's number or chosen — the wire reads an absent
  // currency as IRR and an absent country as Iran, so neither may be left at
  // the Iranian default by accident.
  const iranMarket = isIranMarket();
  const initialCurrency = iranMarket ? 'IRR' : currencyForCountry(null);
  const initialCountry = iranMarket ? DEFAULT_COUNTRY : '';
  const [form, setForm] = React.useState(() => emptyDraft(initialCurrency, initialCountry));
  const [errors, setErrors] = React.useState({});
  const [serverError, setServerError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [adoptFailed, setAdoptFailed] = React.useState(false);
  const [confirmLeave, setConfirmLeave] = React.useState(false);
  const [repairing, setRepairing] = React.useState(false);
  // Doctor track only. `linkFailed` is reported on the closing card rather than
  // thrown, and `enterRef` is the clinic that card's button walks into — held
  // because that button is a user gesture, and the gesture is what the new tab
  // for the doctor panel needs (a `window.open` after an await is blocked).
  const [linkFailed, setLinkFailed] = React.useState(false);
  const [enterRef, setEnterRef] = React.useState('');
  // What the last step chose: the founder's own hours (doctor track) and what
  // «ویزیت» was declared with. A ref, not state, because it is read by
  // `finishDoctorTrack`, which is reached from three different handlers and
  // must see the latest choice.
  const setupRef = React.useRef(null);
  const [setupFailed, setSetupFailed] = React.useState(false);
  const [visitFailed, setVisitFailed] = React.useState(false);

  usePageTitle(t(isAdditional ? 'clinic.start.addClinicTitle' : 'auth.createClinic.title'));

  // E8 — a reload during the roles step lands back on the roles step, not on a
  // form that can only fail.
  //
  // Only a screen still BEFORE the clinic resumes (PM-0195). This runs on every
  // memberships re-read, and the wizard makes those itself after step 3 — the
  // grants refresh, then the access re-read after the join — while the doctor
  // track keeps the crumb until its closing card's button. Unguarded, each one
  // put the founder back on a fresh step 3 (for a non-IRR clinic: an EMPTY,
  // required price), so «اعمال تنظیمات» looped although every write had landed.
  React.useEffect(() => {
    if (quick === 'run' || (phase !== PHASE.DOCTOR && phase !== PHASE.DETAILS)) return;
    let crumb = null;
    try { crumb = JSON.parse(sessionStorage.getItem(RESUME_KEY) || 'null'); } catch { /* a bad crumb is no crumb */ }
    if (!crumb?.clinicId) return;
    const still = memberships.find((m) => m.clinicId === crumb.clinicId);
    if (!still) return;                       // the clinic went away; the crumb is stale
    setCreatedId(crumb.clinicId);
    setCreatedName(crumb.clinicName || still.clinicName || '');
    // The crumb also carries the track. A doctor who reloads on the roles step
    // must still be joined to their clinic at the end of it — without this the
    // reload silently demotes them to the plain track and `enterPanelWith-
    // OwnedClinic` never runs, which is the one step nothing later repairs.
    if (crumb.doctorTrack) setDoctorTrack(true);
    // Step 3 already succeeded in this tab (its choices ride on the crumb): a
    // remount finishes the flow — every step of it idempotent — and never asks
    // for step 3 again.
    if (crumb.setup) {
      setupRef.current = crumb.setup;
      const ref = refOf(still) || still.clinicId;
      if (crumb.doctorTrack) finishDoctorTrack(ref, still.clinicId); else enter(ref);
      return;
    }
    setPhase(PHASE.ROLES);
  }, [memberships]); // eslint-disable-line react-hooks/exhaustive-deps -- reads the phase it runs in, re-runs only on a re-read

  // ── The clinic's country, from the founder's own number (PM-0082) ───────
  //
  // Decided 2026-10-04: preselected from the number the founder signed in
  // with. `myProfile.phone` is that number as Keycloak holds it — never typed
  // here. A number that names no country (national form, none, a failed read)
  // leaves IR. Only an UNTOUCHED field follows the answer: a founder who picked
  // a country before it arrived keeps their pick.
  //
  // The suggestion is also the baseline the leave-guard compares against, so
  // the preselection alone never counts as an unsaved edit.
  const [suggestedCountry, setSuggestedCountry] = React.useState(initialCountry);
  const countryTouched = React.useRef(false);
  React.useEffect(() => {
    let alive = true;
    bffQuery(MY_PHONE)
      .then((data) => {
        const iso = countryForPhone(data?.myProfile?.phone)?.iso2;
        // Outside the Iranian market a +98 number does not make a clinic Iranian.
        if (!alive || !iso || (!iranMarket && iso === 'IR')) return;
        setSuggestedCountry(iso);
        if (!countryTouched.current) setForm((prev) => ({ ...prev, country: iso }));
      })
      .catch(() => { /* IR stays; the founder can still pick */ });
    return () => { alive = false; };
  }, []);

  // A number from abroad: no Iranian defaults anywhere in the wizard (PM-0061).
  // The currency is seeded once the answer arrives, and only over the IRR
  // default — never over a choice the founder already made.
  const region = useAccountRegion();
  const homeCurrency = region.foreign ? currencyForCountry(region.country) : 'IRR';
  const presenceDays = region.foreign ? presenceDaysForCountry(region.country) : DEFAULT_PRESENCE_DAYS;
  // «پزشک نیستم» — the clinic's name and nothing else. Only ever linked from an
  // Iranian «شروع»; a number from abroad that arrives here anyway gets the form.
  const quickName = quick === '1' && !doctorTrack && !region.foreign;

  // "Over the IRR default" is, precisely, over whatever was last seeded: IRR the
  // one time it happens for an Iranian-market brand, and under `market: 'intl'`
  // the USD it starts on, followed by the founder's own once their country is
  // known (IQ → IQD) — never over a currency the founder picked.
  const seededCurrency = React.useRef(initialCurrency);
  React.useEffect(() => {
    if (!region.foreign) return;
    const seeded = seededCurrency.current;
    seededCurrency.current = homeCurrency;
    setForm((prev) => (prev.currency === seeded ? { ...prev, currency: homeCurrency } : prev));
  }, [region.foreign, homeCurrency]);
  // …and the jurisdiction (PM-0082) is the founder's country too: the same
  // number, read by the account, when `myProfile.phone` above has not answered.
  // An untouched field only, exactly like the currency.
  React.useEffect(() => {
    if (!region.foreign || !region.country) return;
    setSuggestedCountry((prev) => (prev === initialCountry ? region.country : prev));
    if (!countryTouched.current) {
      setForm((prev) => (prev.country === initialCountry ? { ...prev, country: region.country } : prev));
    }
  }, [region.foreign, region.country]);

  const dirty = React.useMemo(
    () => Object.entries({ ...emptyDraft(homeCurrency), country: suggestedCountry }).some(([k, v]) => form[k] !== v),
    [form, homeCurrency, suggestedCountry],
  );

  const setField = (key, val) => {
    if (key === 'country') countryTouched.current = true;
    setForm((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));
    setServerError(null);
  };
  const patch = (next) => {
    setForm((prev) => ({ ...prev, ...next }));
    setServerError(null);
  };

  // Back to the clinic they CAME FROM. `defaultRef` is the remembered last clinic,
  // which for someone who opened this from the switcher is the one they were just
  // in; `enterable[0]` is merely the first in display order, and for the owner of
  // several clinics that is often a different one.
  const leave = React.useCallback(() => {
    const back = defaultRef || refOf(enterable[0]);
    navigate(back ? `/c/${back}/` : '/start', { replace: true });
  }, [enterable, defaultRef, navigate]);

  const requestLeave = React.useCallback(() => {
    // Never mid-run: by the time the run's clinic exists the person counts as
    // «additional», and leaving would strand a clinic with no roles or visit.
    if (!isAdditional || phase !== PHASE.DETAILS || submitting || quick === 'run') return;
    if (dirty) { setConfirmLeave(true); return; }
    leave();
  }, [isAdditional, phase, submitting, quick, dirty, leave]);

  // `Esc` closes the wizard the same way the ✕ does — including the unsaved
  // guard. Withheld once the clinic exists: there is nothing left to abandon.
  useShortcut('esc', requestLeave, {
    label: t('common.actions.cancel'),
    group: t('shortcuts.groups.onboarding'),
    enabled: isAdditional && phase === PHASE.DETAILS && !submitting && quick !== 'run',
  });

  /**
   * Doctor track only — run the join, then stop on the closing card.
   *
   * Deliberately reachable from BOTH ways the wizard can reach its end: the
   * ordinary one, and «ادامه بدون به‌روزرسانی» after a failed grants refresh.
   * It used to hang off the first alone, so proceeding past a refresh failure
   * entered the clinic and skipped the join — leaving exactly the founder-who-
   * is-not-a-practitioner this whole track exists to prevent, and leaving it
   * silently, on the one path where something had already gone wrong.
   */
  const finishDoctorTrack = React.useCallback(async (ref, clinicId) => {
    setAdoptFailed(false);
    setPhase(PHASE.LINKING);
    const link = await linkFounderAsPractitioner();
    setLinkFailed(!link);

    // ── The founder's hours, now that there is a membership to hold them ──
    //
    // Only on the link for the clinic just CREATED. The join answers with the
    // founder's first owned clinic, which for a founder of several is not
    // necessarily this one — and writing a new clinic's default week over an
    // older clinic's real one is the one outcome worse than writing nothing.
    //
    // Like the join, it cannot fail the flow: the closing card says so and
    // points at «همکاران», where the same two fields live.
    const { doctor, visit } = setupRef.current || {};
    const ownLink = Boolean(link?.id) && link.clinicId === clinicId;
    let wrote = !doctor;
    if (doctor && ownLink) {
      try {
        await bffMutation(UPDATE_DOCTOR_LINK, { id: link.id, input: doctor }, 'UpdateDoctorLink');
        wrote = true;
      } catch (err) {
        console.error('[onboarding] updateDoctorLink failed:', err?.message || err);
      }
    }
    // A failed join already has its own line; this one is about the hours.
    setSetupFailed(Boolean(link) && !wrote);

    // ── «ویزیت», now that it has somebody to perform it ──────────────────
    //
    // The step declared it inactive because accounting refuses an active
    // service nobody performs. The founder is a performer only once joined, so
    // this is where it is switched on — same row (the upsert is keyed on the
    // catalogue id), same price, the founder as its default performer. Same
    // guard as the hours: never on another clinic's membership.
    let activated = !visit;
    if (visit && ownLink && link.doctorId) {
      try {
        await bffMutation(
          DECLARE_VISIT,
          { input: visitDeclarationInput({ ...visit, doctorId: link.doctorId }) },
          'SetClinicServiceDeclaration',
        );
        activated = true;
      } catch (err) {
        console.error('[onboarding] activating the visit service failed:', err?.message || err);
      }
    }
    setVisitFailed(Boolean(link) && !activated);

    // ── What the join granted, adopted before the way in is offered ──────
    //
    // The join is what makes the founder a practitioner HERE: an accepted
    // clinic_doctors row, and the `doctor` role, which it awaits rather than
    // fires and forgets. The panel read its access before the join ran, so
    // `myAccess.practitionerId` — the one thing that offers «شروع ویزیت» on the
    // founder's own row in پذیرش — came back empty. Entering the clinic does
    // not ask again, because that answer was already for this clinic
    // (ClinicContext re-resolves only when the route names a DIFFERENT one), so
    // the desk offered «ثبت پرداخت» alone until a manual reload. So: refresh
    // until the token carries the role (briefly — see refreshUntilRole), then
    // read access again, both before the closing card's button can be pressed.
    // Neither may fail the flow; the clinic is built and enterable already.
    if (link) {
      await refreshUntilRole('doctor');
      try { await reload({ silent: true }); } catch { /* the next boot re-reads it */ }
    }

    setEnterRef(ref);
    setPhase(PHASE.DONE);
  }, [reload]);

  /** Adopt the grants written by the step just completed, then move on. */
  const adopt = React.useCallback(async (nextPhase, clinicId) => {
    setAdoptFailed(false);
    setPhase(nextPhase);
    try {
      if (nextPhase === PHASE.ADOPTING) await refreshAccessToken();
      const list = await refreshGrants();
      if (nextPhase === PHASE.ADOPTING) { setPhase(PHASE.ROLES); return; }
      const target = (list || []).find((m) => m.clinicId === clinicId);
      const ref = refOf(target) || clinicId;

      // ── The join, and why it is HERE ────────────────────────────────────
      //
      // Last, because it is the only step that needs both halves to exist: the
      // practitioner record from the doctor step and the clinic from this one.
      // After the grants refresh, because it is a doctor-BFF call made with
      // this token and the founder's roles have only just been written.
      //
      // It cannot fail the flow. The clinic is built and enterable by now, so a
      // doctorpreference write that did not land is a reason to say so and
      // offer the retry on the next screen — never a reason to hold a founder
      // out of their own reception desk.
      if (doctorTrack) { await finishDoctorTrack(ref, clinicId); return; }
      enter(ref);
    } catch {
      setAdoptFailed(true);
    }
  }, [refreshGrants, doctorTrack, finishDoctorTrack]); // eslint-disable-line react-hooks/exhaustive-deps

  // Into the clinic — on «اشتراک» instead of its home when this person came from
  // a landing site's «خرید اشتراک» (lib/purchaseIntent.js).
  const enter = (ref) => {
    try { sessionStorage.removeItem(RESUME_KEY); } catch { /* nothing depends on the crumb going */ }
    onSuccess?.(createdId);
    navigate(clinicEntryPath(ref), { replace: true });
  };

  const submitDetails = async () => {
    const { errors: found, input } = validateClinicDraft(form, t);
    if (Object.keys(found).length) { setErrors(found); return; }
    setErrors({});
    setSubmitting(true);
    setServerError(null);
    try {
      const created = await bffMutation(CREATE_CLINIC, { input }, 'CreateClinic');
      const id = created?.createClinic?.id || '';
      const name = created?.createClinic?.name || input.name;
      setCreatedId(id);
      setCreatedName(name);
      try {
        sessionStorage.setItem(RESUME_KEY, JSON.stringify({ clinicId: id, clinicName: name, doctorTrack }));
      } catch { /* the crumb is a convenience, never a requirement */ }
      toast?.success?.(t('auth.createClinic.created', { name }));
      setSubmitting(false);
      adopt(PHASE.ADOPTING, id);
    } catch (err) {
      setServerError(describeError(err, 'createClinic', t));
      setSubmitting(false);
    }
  };

  /** The way out a coded refusal offers. */
  const recover = async (described) => {
    if (described.recovery === RECOVERY.GO_TO_CLINIC) {
      // The refusal names the clinic they already have; go THERE, not merely back.
      const named = described.clinicId && memberships.find((m) => m.clinicId === described.clinicId);
      if (named?.enterable) { navigate(`/c/${refOf(named)}/`, { replace: true }); return; }
      leave();
      return;
    }
    if (described.recovery === RECOVERY.REPAIR && described.clinicId) {
      setRepairing(true);
      try {
        await repairSetup(described.clinicId);
        enter(described.clinicId);
      } catch (err) {
        setServerError(describeError(err, 'createClinic', t));
      } finally {
        setRepairing(false);
      }
    }
  };

  // ── The doctor step's handlers ──────────────────────────────────────────

  /**
   * Adopt the `doctor` role, then open the clinic form.
   *
   * `assignDoctorRole` is fire-and-forget inside `completeOnboardingStep1` — it
   * is logged and not awaited there, deliberately, so a Keycloak blip cannot
   * fail a saved profile. That makes it a RACE with this refresh: a token minted
   * a moment too early carries no `doctor` role.
   *
   * Nothing on the next screen needs that role, so this never blocks. It is done
   * here rather than at the end because the token is refreshed again after the
   * clinic is created, and that second refresh is the one that has to carry both
   * — by which time the grant has had a whole form's worth of time to land.
   */
  const adoptDoctorRole = React.useCallback(async () => {
    setPhase(PHASE.DOCTOR_ADOPTING);
    try { await refreshAccessToken(); } catch { /* the second refresh carries it */ }

    // ── Already a founder: join, do not build a second clinic ─────────────
    //
    // Without this the track walks into the «هر حساب فقط یک کلینیک می‌سازد»
    // refusal below — which is correct about the clinic and wrong about the
    // person. They came here to become a doctor, they now ARE one, and the
    // clinic they founded through this panel has no practitioner record for
    // them. That is precisely what the join repairs, so it runs and the wizard
    // ends where it would have ended anyway.
    if (founderRow && !capabilities.multiFounder) {
      setCreatedId(founderRow.clinicId);
      setCreatedName(founderRow.clinicName || '');
      await finishDoctorTrack(refOf(founderRow) || founderRow.clinicId, founderRow.clinicId);
      return;
    }
    setPhase(PHASE.DETAILS);
  }, [founderRow, capabilities.multiFounder, finishDoctorTrack]);

  /**
   * The practitioner record is written. Two things follow, in this order:
   * the wheel (if a campaign owes one — asked here because this is the moment
   * the BFF has the record the offer is decided from), and the token refresh
   * that adopts the `doctor` role the step just granted.
   */
  const afterIdentity = React.useCallback(async () => {
    const offered = await fetchOfferedDraw();
    if (offered) { setDraw(offered); setPhase(PHASE.PRIZE); return; }
    await adoptDoctorRole();
    // `adoptDoctorRole` and not `[]`: it closes over `founderRow`, which is read
    // from memberships that arrive asynchronously. Pinned at mount, a doctor
    // whose memberships had not loaded yet would be sent to the clinic form to
    // found a second clinic instead of being joined to the one they have.
  }, [adoptDoctorRole]);

  /** «من پزشک نیستم» after all — drop the track and register a clinic only. */
  const skipDoctor = React.useCallback(() => {
    setDoctorTrack(false);
    setPhase(PHASE.DETAILS);
  }, []);

  // The NEW clinic's currency, which prices «ویزیت» on the last step. Read from
  // its membership once the grants are re-read — which also covers a reload on
  // that step, when the form is empty — and from what the form chose until then.
  // Never the panel's own currency: during registration that is whichever
  // clinic the founder came from.
  const createdCurrency = memberships.find((m) => m.clinicId === createdId)?.currency
    || form.currency || 'IRR';

  const steps = doctorTrack ? [
    { key: PHASE.DOCTOR, label: t('auth.steps.doctor') },
    { key: PHASE.DETAILS, label: t('auth.steps.details') },
    { key: PHASE.ROLES, label: t('auth.steps.roles') },
  ] : [
    { key: PHASE.DETAILS, label: t('auth.steps.details') },
    { key: PHASE.ROLES, label: t('auth.steps.roles') },
  ];

  const onDoctor = phase === PHASE.DOCTOR || phase === PHASE.PRIZE || phase === PHASE.DOCTOR_ADOPTING;
  const onRoles = phase === PHASE.ROLES || phase === PHASE.ENTERING;
  const onClosing = phase === PHASE.LINKING || phase === PHASE.DONE;

  const currentStep = onDoctor ? PHASE.DOCTOR : (onRoles || onClosing) ? PHASE.ROLES : PHASE.DETAILS;
  const completedSteps = onDoctor ? []
    : onClosing ? [PHASE.DOCTOR, PHASE.DETAILS, PHASE.ROLES]
      : onRoles ? [PHASE.DOCTOR, PHASE.DETAILS]
        : [PHASE.DOCTOR];

  const shell = (children, { subtitle, closable = true } = {}) => (
    <AuthShell
      width="lg"
      align="top"
      title={t(isAdditional ? 'clinic.start.addClinicTitle' : 'auth.createClinic.title')}
      subtitle={subtitle}
      testId="onboarding"
      headerEnd={closable && isAdditional && phase === PHASE.DETAILS ? (
        <IconButton
          icon={<X size={18} />}
          label={t('common.actions.cancel')}
          variant="onGradient"
          size={38}
          onClick={requestLeave}
          disabled={submitting}
        />
      ) : null}
    >
      {children}
    </AuthShell>
  );

  // ── The run, ahead of every guard below ───────────────────────────────────
  //
  // Once the run has created the clinic, the person IS a founder — and the
  // one-clinic guard below would take the screen over mid-run. The run makes
  // its own decisions about an existing or half-built clinic (quickSetup.js).
  // Iranian accounts only (an IRR visit price, the council's province): a brand
  // outside the Iranian market never runs it, even from a pasted URL.
  if (quick === 'run' && iranMarket) {
    return shell(<QuickSetup />, { subtitle: t('clinic.quickSetup.subtitle'), closable: false });
  }

  // ── E1 · A half-built clinic is finished, never duplicated ────────────────
  //
  // Offering «ایجاد کلینیک جدید» to a founder whose saga half-applied is the move
  // that leaves them with TWO clinics of one name — the create no longer collides,
  // it succeeds, and the half-built one stays a husk nobody can enter. The only
  // correct action is the repair, so it is the only one offered.
  if (halfBuilt && phase === PHASE.DETAILS) {
    return shell(
      <SystemPage
        tone="warning"
        icon={<Wrench size={26} aria-hidden="true" />}
        title={t('clinic.gate.repairTitle', { name: halfBuilt.clinicName })}
        body={t('clinic.gate.repairBody')}
        testId="onboarding-repair"
        actions={[{
          key: 'repair',
          label: repairing ? t('clinic.gate.repairing') : t('clinic.gate.repairAction'),
          onClick: async () => {
            setRepairing(true);
            try { await repairSetup(halfBuilt.clinicId); enter(refOf(halfBuilt) || halfBuilt.clinicId); } finally { setRepairing(false); }
          },
        }]}
      />,
    );
  }

  // ── F1 · One clinic per account, where the platform says so ──────────────
  //
  // `multiFounder` is the BFF's CLINIC_MULTI_FOUNDER, and the BFF enforces it:
  // with it off, `createClinic` refuses a founder with CLINIC_ALREADY_EXISTS. This
  // screen says so up front rather than after a filled-in form. With it on, a
  // founder falls through to the ordinary wizard.
  if (founderRow && !capabilities.multiFounder && phase === PHASE.DETAILS) {
    return shell(
      <SystemPage
        tone="info"
        icon={<Ban size={26} aria-hidden="true" />}
        title={t('auth.createClinic.oneClinicTitle')}
        body={t('auth.createClinic.oneClinicBody', { name: founderRow.clinicName })}
        testId="onboarding-single-clinic"
        actions={[
          {
            key: 'go',
            label: t('clinic.gate.goToOther', { name: founderRow.clinicName }),
            onClick: () => navigate(`/c/${refOf(founderRow)}/`, { replace: true }),
          },
        ]}
      />,
    );
  }

  return (
    <>
      {shell(
        <>
          {quickName ? null : (
            <StepIndicator
              steps={steps}
              current={currentStep}
              completed={completedSteps}
              ariaLabel={t('auth.steps.ariaLabel')}
            />
          )}

          {/* ── The doctor half ──────────────────────────────────────────
              Rendered before the clinic form and never beside it: they are
              separate steps of one wizard, and the council lookup has to have
              written a practitioner record before `createClinic` runs, so the
              founder the join finds at the end is a doctor. */}
          {onDoctor && phase !== PHASE.DOCTOR_ADOPTING ? (
            <DoctorTrack
              phase={phase === PHASE.PRIZE ? 'prize' : 'doctor'}
              draw={draw}
              onIdentityDone={afterIdentity}
              onSkip={skipDoctor}
              onPrizeDone={adoptDoctorRole}
              disabled={offline}
            />
          ) : null}

          {/* No retry offered here, unlike the clinic's adoptions: nothing on
              the next screen is scoped to the `doctor` role, and the refresh
              after the clinic is created carries it anyway. */}
          {phase === PHASE.DOCTOR_ADOPTING ? (
            <AccessRefreshPanel
              state="refreshing"
              successTitle={t('auth.onboarding.doctorSaved')}
              testId="doctor-access-refresh"
            />
          ) : null}

          {phase === PHASE.DETAILS && quickName ? (
            <QuickClinicNameStep disabled={offline} />
          ) : null}

          {phase === PHASE.DETAILS && !quickName ? (
            <ClinicDetailsStep
              form={form}
              region={region}
              onChange={setField}
              onPatch={patch}
              errors={errors}
              serverError={serverError}
              onSubmit={submitDetails}
              onRecover={recover}
              submitting={submitting || repairing}
              disabled={offline}
            />
          ) : null}

          {phase === PHASE.ADOPTING ? (
            <AccessRefreshPanel
              state={adoptFailed ? 'failed' : 'refreshing'}
              successTitle={t('auth.createClinic.created', { name: createdName })}
              onRetry={() => adopt(PHASE.ADOPTING, createdId)}
              // Walking on without the refresh is deliberately available. The
              // clinic exists; the gate has named states for a stale token and
              // for a half-applied setup, and either of them is a better place
              // to be than a wizard that can only offer the same retry again.
              onProceed={() => setPhase(PHASE.ROLES)}
              proceedLabel={t('auth.createClinic.continueAnyway')}
            />
          ) : null}

          {/* A stale token surfaces from THREE places on this step — the
              template load, the seeding call, and the final adoption — and all
              three mean the same thing and have the same fix. The panel is
              rendered above the step rather than below it so the offer is
              visible without scrolling past five role tiles.

              Before this it was only drawn while ENTERING, so a FORBIDDEN from
              the roles step itself set a flag that nothing rendered: the step
              showed «دسترسی‌های شما هنوز به‌روز نشده است» and the one control
              that could fix it was nowhere on screen. */}
          {onRoles && adoptFailed ? (
            <AccessRefreshPanel
              state="failed"
              onRetry={() => adopt(phase === PHASE.ENTERING ? PHASE.ENTERING : PHASE.ADOPTING, createdId)}
              onProceed={() => (doctorTrack && phase === PHASE.ENTERING
                ? finishDoctorTrack(createdId, createdId)
                : enter(createdId))}
              proceedLabel={t('auth.createClinic.enterWithoutRoles')}
            />
          ) : null}

          {/* The doctor wizard ends on a success card — a green check, a line, and
              the way in. Here the way in is automatic (the grants are being
              adopted), so the card says «entering» instead of offering a button;
              a failed adoption falls back to the roles step and its panel above. */}
          {phase === PHASE.ENTERING && !adoptFailed ? (
            <OnboardingSuccess
              title={t('auth.createClinic.created', { name: createdName })}
              body={t('auth.createClinic.entering')}
            />
          ) : null}

          {onRoles && !(phase === PHASE.ENTERING && !adoptFailed) ? (
            <ClinicRolesStep
              // Re-seeded when either default changes — the currency, or the
              // working days once "abroad" is known after a reload on this step.
              key={`${createdCurrency}|${presenceDays.join()}`}
              currency={createdCurrency}
              presenceDays={presenceDays}
              doctorTrack={doctorTrack}
              disabled={offline || phase === PHASE.ENTERING}
              onStaleAccess={() => setAdoptFailed(true)}
              onDone={(setup) => {
                setupRef.current = setup;
                rememberSetup(setup);
                adopt(PHASE.ENTERING, createdId);
              }}
            />
          ) : null}

          {phase === PHASE.LINKING ? (
            <OnboardingSuccess
              title={t('auth.createClinic.created', { name: createdName })}
              body={t('auth.onboarding.linking')}
              testId="onboarding-linking"
            />
          ) : null}

          {/* ── The doctor track's closing card ────────────────────────────
              The plain track enters by itself; this one STOPS here, and both
              reasons are the same reason. The doctor panel opens in a new tab
              (lib/doctorPanel.js) and `window.open` called after an await is a
              popup with no gesture behind it — blocked in every browser that
              has the setting on. So the tab is opened by this button, and the
              person chooses whether they want it at all.

              «ورود به پنل کلینیک» is the primary: they registered a clinic and
              that is where they were going. */}
          {phase === PHASE.DONE ? (
            <OnboardingSuccess
              title={t('auth.createClinic.created', { name: createdName })}
              body={linkFailed ? t('auth.onboarding.linkFailed') : t('auth.onboarding.joined')}
              testId="onboarding-done"
            >
              {setupFailed ? (
                <Alert variant="warning" data-testid="onboarding-hours-failed">
                  {t('auth.onboarding.hoursFailed')}
                </Alert>
              ) : null}
              {visitFailed ? (
                <Alert variant="warning" data-testid="onboarding-visit-failed">
                  {t('auth.onboarding.visitFailed')}
                </Alert>
              ) : null}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                <PrimaryAction
                  fullWidth
                  onClick={() => enter(enterRef || createdId)}
                  data-testid="onboarding-enter-clinic"
                >
                  {t('auth.onboarding.enterClinicPanel')}
                </PrimaryAction>
                {/* Withheld when the join did not land: the doctor panel would
                    open on «عضو هیچ کلینیکی نیستید», which is a worse answer
                    than not offering the tab. The clinic is unaffected. */}
                {linkFailed ? null : (
                  <Button
                    variant="outline"
                    onClick={() => openDoctorPanel(doctorHomeUrl())}
                    data-testid="onboarding-open-doctor-panel"
                  >
                    <ExternalLink size={15} aria-hidden="true" />
                    {t('auth.onboarding.openDoctorPanel')}
                  </Button>
                )}
              </div>
            </OnboardingSuccess>
          ) : null}
        </>,
        {
          subtitle: onDoctor ? t('auth.steps.doctor')
            : onClosing ? t('auth.onboarding.complete')
              : onRoles ? t('auth.steps.roles')
                : t('auth.createClinic.subtitle'),
        },
      )}

      <ConfirmDialog
        open={confirmLeave}
        onOpenChange={setConfirmLeave}
        title={t('auth.createClinic.discardTitle')}
        description={t('auth.createClinic.discardBody')}
        confirmLabel={t('auth.createClinic.discardConfirm')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={() => { setConfirmLeave(false); leave(); }}
      />
    </>
  );
}
